import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  안전 — 밀폐공간 가스안전 (예정)  (/safety/gas)
//  [2026-10-08] 기획서 v7 3-5. ①적용시나리오 ②제품구조 ③안전기능 ④방폭·법적범위 ⑤통합관제 ⑥출시상태.
//  [2026-10-22 사용자] ★본문 화이트화(SafetyEnv.module). 히어로 다크 유지 / 본문 화이트. ②노드체인 ③원형뱃지.
//    주의(block 241): '가스농도측정기' 등 확정 명칭 검토. 방폭·법정측정 범위는 확정 후 표시.
// ============================================================================

const HERO_IMG = '/images/hero/hero-safety.jpg';
const SLOGAN = '밀폐공간의 유해가스를 실시간 측정하고, 현장 경보·무선 관제·비상 연락까지 연결해 작업자의 질식·중독 사고를 예방하는 가스안전 솔루션입니다. (개발 중)';
const INQ = '/support/inquiry?product=' + encodeURIComponent('밀폐공간 가스안전') + '&from=/safety/gas';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function SafetyGas() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '안전', '밀폐공간 가스안전']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>밀폐공간 가스안전</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 적용 시나리오 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>SCENARIO</p>
            <h2 className={w.h2}>작업 전 측정, 작업 중 모니터링</h2>
            <p className={w.lead}>밀폐공간은 산소결핍·유해가스 중독 위험이 큽니다. 작업 전 가스 농도를 측정하고, 작업 중에는 지속 모니터링하는 시나리오로 개발하고 있습니다.</p>
            <a className={w.moreLink} onClick={() => nav('/support/consult')}>상담하기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 제품 구조 — 노드 체인(측정→표시→전송) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>STRUCTURE</p>
            <h2 className={w.h2}>제품 구조</h2>
            <div className={w.nodeRow3}>
              {[
                { l: '가스센서·펌프·호스', i: 'air', d: '가스 농도를 측정하는 센서와 흡입 펌프·호스(예정).', c: w.cTeal },
                { l: 'LCD·경보', i: 'monitor', d: '현장 LCD 표시와 경보 출력(예정).', c: w.cBlue },
                { l: 'LTE·중계', i: 'signal', d: 'LTE 통신과 중계로 원격 관제(예정).', c: w.cIndigo },
              ].map((x) => (
                <div key={x.l} className={`${w.nodeCell} ${x.c}`}>
                  <div className={w.nodeDot}>{GUIDE_ICON[x.i]}</div>
                  <h3>{x.l}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
            <Soon label="제품 개념도" />
          </Reveal>
        </div>
      </section>

      {/* ③ 안전 기능 — 채움 원형 뱃지(4종) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>SAFETY FEATURES</p>
            <h2 className={w.h2}>안전 기능</h2>
            {/* 단순 틴트 카드(다이어그램 아님) */}
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cCoral} ${w.solid}`}><Ic n="alert" /><h3>비상 버튼</h3><p>작업자가 즉시 도움을 요청(예정).</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="bell" /><h3>관제 알림</h3><p>위험 발생 시 관제에 즉시 알림(예정).</p></div>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="phone" /><h3>음성 연락</h3><p>현장과 음성으로 연락(예정).</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="signal" /><h3>현장 경보</h3><p>현장에서 경보로 위험 전파(예정).</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 방폭·법적 범위 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CERTIFICATION &amp; LAW</p>
            <h2 className={w.h2}>방폭 · 법적 범위</h2>
            <p className={w.lead}>방폭 인증 취득 상태와 법정 측정 적용 범위는 확정 후 명확히 표시합니다. 확정 전에는 인증·법적 적합을 단정하지 않습니다. (제품명은 '가스농도측정기' 등 확정 명칭으로 표기 예정)</p>
            <Soon label="방폭 계획·인증 상태" />
          </Reveal>
        </div>
      </section>

      {/* ⑤ 통합관제 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>INTEGRATED CONTROL</p>
            <h2 className={w.h2}>통합 관제</h2>
            <p className={w.lead}>가스 농도·경보·장비 상태·이력을 통합 관제 플랫폼에서 관리할 수 있도록 편입을 준비하고 있습니다.</p>
            <a className={w.moreLink} onClick={() => nav('/safety/platform')}>플랫폼 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑥ 출시 상태 — CTA 틴트밴드(상단 시안 악센트 내장) */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>기술·공동개발을 문의하세요</h2>
            <p className={w.lead}>확정 기능과 개발 검토 기능을 구분해 안내합니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav(INQ)}>기술 문의</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/consult')}>공동개발 문의</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
